<template>
  <div v-if="totalPages > 1" class="base-pagination" :class="{ 'base-pagination--flat': flat }">
    <button
        type="button"
        class="base-pagination__nav"
        :disabled="current <= 1"
        title="이전 페이지"
        aria-label="이전 페이지"
        @click="go(current - 1)"
    >
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 5 8 12l7 7" /></svg>
    </button>

    <span class="base-pagination__nums">
      <template v-for="(p, i) in pageItems" :key="i">
        <span v-if="p === null" class="base-pagination__gap">…</span>
        <button
            v-else
            type="button"
            class="base-pagination__num"
            :class="{ 'is-current': p === current }"
            :aria-current="p === current ? 'page' : undefined"
            @click="go(p)"
        >{{ p }}</button>
      </template>
    </span>

    <button
        type="button"
        class="base-pagination__nav"
        :disabled="current >= totalPages"
        title="다음 페이지"
        aria-label="다음 페이지"
        @click="go(current + 1)"
    >
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 5 7 7-7 7" /></svg>
    </button>

    <span v-if="totalText" class="base-pagination__text">{{ totalText }}</span>
  </div>
</template>

<script>
export default {
  name: 'BasePagination',
  props: {
    // 현재 페이지 (1-base)
    page: { type: [Number, String], default: 1 },
    // 마지막 페이지 번호. 1 이하면 컴포넌트 전체가 렌더되지 않음
    lastPage: { type: [Number, String], default: 1 },
    // 총 건수. null이면 '총 N건' 텍스트 생략
    total: { type: [Number, String], default: null },
    // 현재 페이지 주변에 노출할 페이지 번호 총 개수(현재 페이지 포함). 5 = 앞2 + 현재 + 뒤2
    windowSize: { type: [Number, String], default: 5 },
    // 배경/상단선 제거 (본문 안에 인라인으로 놓일 때)
    flat: { type: Boolean, default: false },
  },
  emits: ['change'],
  computed: {
    totalPages() {
      const n = parseInt(this.lastPage, 10)
      return Number.isFinite(n) && n > 0 ? n : 1
    },
    current() {
      const n = parseInt(this.page, 10)
      if (!Number.isFinite(n)) return 1
      return Math.min(Math.max(n, 1), this.totalPages)
    },
    // [1, null, ...현재 주변 windowSize개..., null, last] — null은 생략(…) 구간
    pageItems() {
      const last = this.totalPages
      const size = Math.max(1, parseInt(this.windowSize, 10) || 1)
      // 현재 페이지를 가운데 두고 size개. 양 끝에서는 밀어서 항상 size개를 유지
      let start = this.current - Math.floor((size - 1) / 2)
      let end = start + size - 1
      if (start < 1) {
        start = 1
        end = Math.min(last, size)
      }
      if (end > last) {
        end = last
        start = Math.max(1, last - size + 1)
      }
      const items = []
      if (start > 1) {
        items.push(1)
        if (start > 2) items.push(null)
      }
      for (let p = start; p <= end; p++) items.push(p)
      if (end < last) {
        if (end < last - 1) items.push(null)
        items.push(last)
      }
      return items
    },
    totalText() {
      if (this.total === null || this.total === '') return ''
      const n = Number(this.total)
      if (!Number.isFinite(n)) return ''
      return `총 ${n.toLocaleString()}건`
    },
  },
  methods: {
    go(p) {
      const target = Math.min(Math.max(parseInt(p, 10) || 1, 1), this.totalPages)
      if (target === this.current) return
      this.$emit('change', target)
    },
  },
}
</script>

<style lang="scss" scoped>
@use '../../scss/variables' as *;

.base-pagination {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 6px;
  padding: 6px;
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
}

.base-pagination--flat {
  background: transparent;
  border-top: 0;
}

/* 이전/다음 — 숫자 버튼과 같은 크기/테두리라 한 덩어리로 읽힌다 */
.base-pagination__nav {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  padding: 0;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  background: #fff;
  color: #475569;
  cursor: pointer;
  box-sizing: border-box;

  svg {
    width: 16px;
    height: 16px;
    fill: none;
    stroke: currentColor;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  &:hover:not(:disabled) {
    background: $color-primary-50;
    border-color: $color-primary;
    color: $color-primary;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

.base-pagination__nums {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 4px;
  margin: 0 4px;
}

.base-pagination__num {
  min-width: 30px;
  height: 30px;
  padding: 0 6px;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  background: #fff;
  font-size: 13px;
  font-weight: 500;
  color: #475569;
  cursor: pointer;
  box-sizing: border-box;

  &:hover:not(.is-current) {
    background: $color-primary-50;
    border-color: $color-primary;
    color: $color-primary;
  }

  &.is-current {
    background: $color-primary;
    border-color: $color-primary;
    color: #fff;
    font-weight: 700;
    cursor: default;
  }
}

.base-pagination__gap {
  padding: 0 2px;
  font-size: 13px;
  color: #94a3b8;
  user-select: none;
}

.base-pagination__text {
  margin-left: 6px;
  font-size: 13px;
  font-weight: 500;
  color: #475569;
}
</style>
